// src/pages/Profile/index.tsx
import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { User, Mail, Calendar, Shield, Plus, MapPin, Link, Package } from 'lucide-react';
import { useAddress } from '../../contexts/AddressContext';
import type { Address, City } from '../../types';
import userAPI from '../../api/user';
import { showToast } from '../../utils/toast';
import { NameVerificationSection } from '../../components/profile/NameVerificationSection';
import useLocationCapture from '../../utils/locationCapture';
import { formatCoord } from '../../utils/utilityFunctions';
import { Settings } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import AddressesTab from '../../components/profile/AddressesTab';
import ProfileTab from '../../components/profile/ProfileTab';
import addressAPI from '../../api/addresses';
// import SecurityTab from '../../components/profile/SecurityTab';
// import OrdersTab from '../../components/profile/OrdersTab';

const ProfilePage: React.FC = () => {
    const location = useLocation();
    const { user, updateUser, refreshUser } = useAuth();
    const [activeTab, setActiveTab] = useState<'profile' | 'addresses' | 'security' | 'orders'>('profile');
    const tabs = [
        { id: 'profile', label: 'Profile', icon: User },
        { id: 'addresses', label: 'Addresses', icon: MapPin },
        { id: 'security', label: 'Security', icon: Shield },
        { id: 'orders', label: 'Orders', icon: Package },
    ];
    const {
        addresses,
        deleteAddress: contextDeleteAddress,
        setDefaultAddress: contextSetDefaultAddress,
        addAddress: contextAddAddress,
        updateAddress: contextUpdateAddress,
        isLoading,
        setIsLoading,
        fetchAddresses,
        updateAddressCoordinates: contextUpdateAddressCoordinates,
    } = useAddress();

    const handleUpdateName = async (nameData: {
        first_name: string;
        last_name: string;
        middle_name?: string;
    }) => {
        try {
            const response = await userAPI.updateProfile(nameData);
            if (response.data) {
                // Update auth context
                updateUser(response.data);

                showToast('Name updated successfully', 'success');
            } else if (response.error) {
                showToast(response.error, 'error');
            }
        } catch (error: any) {
            showToast('Failed to update name', 'error');
        }
    };

    const handleIdUpload = async (file: File) => {
        try {
            const response = await userAPI.uploadIdDocument(file);
            if (response.data) {
                // Update user with new verification status
                updateUser({
                    id_verification_status: response.data.verification_status,
                    id_uploaded_at: response.data.uploaded_at,
                });
                await refreshUser();
                showToast(response.data.message || 'ID document uploaded for verification', 'success');
            } else if (response.error) {
                showToast(response.error, 'error');
            }
        } catch (error: any) {
            showToast('Failed to upload ID', 'error');
        }
    };

    const fetchUserProfile = async () => {
        try {
            const response = await userAPI.getProfile();
            if (response.data) {
                updateUser(response.data); // Use context update method
            }
        } catch (error) {
            console.error('Failed to fetch profile:', error);
            showToast('Failed to load profile', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!user) {
            fetchUserProfile();
        }
    }, [user]);

    useEffect(() => {
        // Parse query parameters
        const params = new URLSearchParams(location.search);
        const tab = params.get('tab');

        // Set active tab if valid
        if (tab && ['profile', 'addresses', 'security', 'orders'].includes(tab)) {
            setActiveTab(tab as any);
        }
    }, [location]);

    if (!user) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading profile...</p>
                </div>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="py-8">
                <div className="animate-pulse space-y-4">
                    <div className="h-24 bg-gray-200 rounded"></div>
                    <div className="h-24 bg-gray-200 rounded"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="px-6 py-8 bg-gradient-to-r from-primary-50 to-primary-100">
                    <div className="flex items-center">
                        <div className="h-20 w-20 rounded-full bg-white border-4 border-white shadow-sm flex items-center justify-center">
                            {user.profile_picture ? (
                                <img
                                    src={user.profile_picture}
                                    alt={`${user.first_name} ${user.last_name}`}
                                    className="h-16 w-16 rounded-full"
                                />
                            ) : (
                                <User className="h-12 w-12 text-primary-600" />
                            )}
                        </div>
                        <div className="ml-6">
                            <h1 className="text-2xl font-bold text-gray-900">
                                {user.first_name} {user.last_name}
                            </h1>
                            <p className="text-gray-600 mt-1">{user.email}</p>
                            <div className="flex flex-wrap gap-2 mt-2">
                                {user.roles?.map((role) => (
                                    <span
                                        key={role}
                                        className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-800"
                                    >
                                        <Shield className="h-3 w-3 mr-1" />
                                        {role.charAt(0).toUpperCase() + role.slice(1)}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
                {/* Tab Navigation */}
                <div className="flex border-b border-gray-200 mb-6 overflow-x-auto">
                    {tabs.map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id as any)}
                            className={`flex items-center px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === tab.id
                                ? 'border-primary-600 text-primary-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            <tab.icon className="h-4 w-4 mr-2" />
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* Tab Content */}
                {activeTab === 'profile' && <ProfileTab user={user} onNameSubmit={handleUpdateName} onIdUpload={handleIdUpload} />}
                {activeTab === 'addresses' && <AddressesTab />}
                {activeTab === 'security' && <SecurityTab />}
                {activeTab === 'orders' && <OrdersTab />}

                {/* Quick actions */}
                <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                    <button className="bg-white p-4 rounded-lg shadow-sm border text-left hover:bg-gray-50 transition-colors">
                        <div className="text-sm font-medium text-gray-900">Order History</div>
                        <div className="text-sm text-gray-500 mt-1">View your past orders</div>
                    </button>

                    <button className="bg-white p-4 rounded-lg shadow-sm border text-left hover:bg-gray-50 transition-colors">
                        <div className="text-sm font-medium text-gray-900">Saved Items</div>
                        <div className="text-sm text-gray-500 mt-1">Items you've saved for later</div>
                    </button>

                    <button className="bg-white p-4 rounded-lg shadow-sm border text-left hover:bg-gray-50 transition-colors">
                        <div className="text-sm font-medium text-gray-900">Account Settings</div>
                        <div className="text-sm text-gray-500 mt-1">Update your preferences</div>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ProfilePage;